import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MemberCard } from '@/components/team/member-card';
import { useAuth } from '@/hooks/use-auth';
import { trpc } from '@/trpc';
import { Skeleton } from '@/components/ui/skeleton';

export function TeamIndexPage() {
  const { user } = useAuth();
  const role = (user as any)?.role;
  const isAdmin = role === 'admin';
  const isAdminOrDirector = role === 'admin' || role === 'director';

  const { data: members, isLoading } = (trpc as any).team?.list?.useQuery?.() ?? { data: [], isLoading: false };
  const { data: portcoData, isLoading: portcoLoading } = (trpc as any).team?.portcoBreakdown?.useQuery?.(
    undefined,
    { enabled: isAdminOrDirector },
  ) ?? { data: undefined, isLoading: false };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team</h1>
          <p className="text-muted-foreground">Manage your team members and roles.</p>
        </div>
        {isAdmin && (
          <Link to="/team/invite">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Invite member
            </Button>
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : !members || members.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          No team members yet. Invite your first team member to get started.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {members.map((member: any) => (
            <Link key={member.id} to="/team/$memberId" params={{ memberId: member.userId }}>
              <MemberCard member={member} />
            </Link>
          ))}
        </div>
      )}

      {isAdminOrDirector && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Portco Revenue</h2>
          {portcoLoading ? (
            <Skeleton className="h-24 rounded-xl" />
          ) : !portcoData || portcoData.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No portco-tagged deals found. Tag deals with a portco ID to see a breakdown here.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left font-medium">Portco</th>
                    <th className="px-4 py-3 text-right font-medium">Deals</th>
                    <th className="px-4 py-3 text-right font-medium">Revenue (CAD)</th>
                  </tr>
                </thead>
                <tbody>
                  {portcoData.map((row: any) => (
                    <tr key={row.portcoId} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{row.portcoId}</td>
                      <td className="px-4 py-3 text-right">{row.dealCount}</td>
                      <td className="px-4 py-3 text-right">
                        CA${Number(row.totalRevenueCad).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
